import type { ApiError } from '@/types/auth';
import type { AngleDeleteConflictMeta } from '@/types/project-angle';
import { apiLogger } from './logger';
import { authEvents } from './auth-events';
import { AuthTokenService } from './auth-token-service';
import { resolveApiUrl } from './api-host';

// Resolved per-call (not memoized at module scope) so the Russia mirror can key
// off the runtime host — see resolveApiUrl. Cheap: a hostname check + string.
function apiUrl(): string {
  return resolveApiUrl();
}

/**
 * Build the X-Device-* request headers for the current browser.
 *
 * `X-Device-Type` is best-effort form-factor — phone/tablet/desktop — derived
 * from `navigator.userAgentData` when available, with a coarse touch + UA
 * fallback for Safari / Firefox. `X-Device-Model` is intentionally omitted
 * on web: browsers do not expose the device model without an async
 * `getHighEntropyValues({model})` call, and even then most user agents
 * return an empty string. The API treats both headers as optional.
 *
 * SSR-safe: returns `{}` when `navigator` is unavailable so Next.js
 * server-render and edge runtimes don't crash.
 */
function getDeviceHeaders(): Record<string, string> {
  if (typeof navigator === 'undefined') {
    return {};
  }

  const ua = navigator.userAgent ?? '';
  const userAgentData = (navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  }).userAgentData;

  const isTablet =
    /iPad/i.test(ua) ||
    (/Android/i.test(ua) && !/Mobile/i.test(ua)) ||
    // Modern iPadOS reports as Mac in Safari; touch-point count separates a
    // real iPad from an actual Mac (which has 0 touch points).
    (/Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);

  const isMobile =
    !isTablet &&
    (userAgentData?.mobile === true ||
      /Mobi|iPhone|iPod|Android.*Mobile/i.test(ua));

  const deviceType = isTablet ? 'tablet' : isMobile ? 'phone' : 'desktop';

  return { 'X-Device-Type': deviceType, ...getThemeHeader() };
}

/**
 * Report the RESOLVED interface theme, so conversion analysis can ask whether
 * the people who buy are the people looking at a dark screen.
 *
 * Read from the `.dark` class the inline boot script in app/layout.tsx puts on
 * <html> — that class already reflects the stored preference with `system`
 * resolved against `prefers-color-scheme`, which is exactly what is on screen.
 * SSR-safe: no document, no header.
 */
function getThemeHeader(): Record<string, string> {
  if (typeof document === 'undefined') {
    return {};
  }

  return {
    'X-Client-Theme': document.documentElement.classList.contains('dark')
      ? 'dark'
      : 'light',
  };
}

/**
 * Wall-clock deadline for every API call.
 *
 * WHY THIS EXISTS: a `fetch` that never settles — mobile network switch, a tab
 * frozen by iOS Safari, a dead keep-alive socket through the tunnel — leaves
 * TanStack Query stuck in `fetching` forever. Query dedupes concurrent fetches,
 * so no refetch, retry, focus or reconnect trigger can replace the hung one:
 * the screen shows a spinner with no error, no retry and no way out (observed
 * in production, see the concept page). The deadline converts that silent hang
 * into a normal failure the retry/error UI can act on.
 *
 * 20s is deliberately tighter than Android's OkHttp `readTimeout` (30s) and
 * Apple's URLSession default (60s): the API answers in 0.03-0.5s in production
 * (verified across a full session in the Caddy access log), so anything past
 * 20s is a wedged connection, not a slow endpoint — waiting longer only makes
 * the user stare at a spinner. File uploads and blob downloads bypass this
 * client entirely (raw `fetch` in image-upload.service / estimate export), so
 * they are unaffected.
 */
const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

interface FetchOptions extends RequestInit {
  token?: string;
  skipAuth?: boolean;
  /**
   * Demote API/network error logs from `error` to `debug` for this call.
   * Use only when a non-2xx (typically 404) is part of the expected response
   * surface — e.g. the loading-facts cold-load, where a deleted/cross-account
   * project must not blow up Sentry. ApiClientError is still thrown; only
   * the telemetry side is silenced.
   */
  silentErrors?: boolean;
  /**
   * Statuses that are part of THIS endpoint's normal answer surface: their
   * logs are demoted to `debug`, every other status still logs as `error`.
   * Narrower than `silentErrors`, which silences a 500 just as happily — use
   * this when exactly one status is control flow (e.g. GET /user answering
   * 403 while the address is unconfirmed, which is how the app discovers that
   * state) and the rest must stay loud.
   */
  expectedStatuses?: number[];
  /**
   * Per-call deadline override in ms. `0` disables the deadline — only for
   * endpoints that legitimately stream for minutes. Defaults to
   * `DEFAULT_REQUEST_TIMEOUT_MS`.
   */
  timeoutMs?: number;
}

/**
 * Combine the caller's `AbortSignal` (TanStack Query cancellation) with a
 * timeout of our own. Returns the merged signal plus a `didTimeout` probe so
 * the caller can tell an intentional cancellation (rethrow as-is, no retry)
 * from a deadline breach (surface as a retryable network failure).
 *
 * Hand-rolled rather than `AbortSignal.any` + `AbortSignal.timeout`: those need
 * Safari 17.4+, and this path must also hold on older mobile browsers — exactly
 * the population that hits hung sockets most often.
 */
function createDeadline(
  external: AbortSignal | null | undefined,
  timeoutMs: number
): { signal: AbortSignal; didTimeout: () => boolean; release: () => void } {
  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onExternalAbort = () => controller.abort();
  if (external) {
    if (external.aborted) {
      controller.abort();
    } else {
      external.addEventListener('abort', onExternalAbort);
    }
  }

  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    release: () => {
      clearTimeout(timer);
      external?.removeEventListener('abort', onExternalAbort);
    },
  };
}

/**
 * Report a request deadline breach to Sentry.
 *
 * Timeouts are the one network failure worth paging on: a plain offline error
 * is expected and self-heals, but a request that hangs past 30s while the API
 * is healthy means the client is wedged — the exact class of incident that was
 * previously invisible (apiLogger only writes to the console, so hung screens
 * never reached any dashboard). Throttled per endpoint so a flapping
 * connection can't flood the project.
 */
const timeoutReportedAt = new Map<string, number>();
const TIMEOUT_REPORT_THROTTLE_MS = 60_000;

function reportTimeoutToSentry(
  endpoint: string,
  method: string,
  timeoutMs: number
): void {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const last = timeoutReportedAt.get(endpoint);
  if (last && now - last < TIMEOUT_REPORT_THROTTLE_MS) return;
  timeoutReportedAt.set(endpoint, now);

  void import('@sentry/nextjs')
    .then((Sentry) => {
      Sentry.captureMessage(`API request timed out: ${method} ${endpoint}`, {
        level: 'warning',
        tags: { api_timeout: 'true', endpoint, method },
        extra: { timeoutMs },
      });
    })
    .catch(() => {
      // Sentry unavailable (blocked, not configured) — the timeout itself is
      // already surfaced to the user, telemetry is best-effort.
    });
}

interface RequestInterceptor {
  onRequest?: (
    config: RequestInit & { url: string }
  ) => (RequestInit & { url: string }) | Promise<RequestInit & { url: string }>;
  onError?: (error: ApiClientError) => void | Promise<void>;
}

class ApiClient {
  private token: string | null = null;
  private locale: string | null = null;
  private interceptors: RequestInterceptor[] = [];

  constructor() {
    if (typeof window !== 'undefined') {
      authEvents.on('token:updated', (event) => {
        this.setToken(event.payload as string | null);
      });
    }
  }

  setToken(token: string | null) {
    apiLogger.debug(
      'Setting token:',
      token ? `${token.substring(0, 20)}...` : 'null'
    );
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  setLocale(locale: string | null) {
    this.locale = locale;
  }

  addInterceptor(interceptor: RequestInterceptor) {
    this.interceptors.push(interceptor);
  }

  removeInterceptor(interceptor: RequestInterceptor) {
    const index = this.interceptors.indexOf(interceptor);
    if (index > -1) {
      this.interceptors.splice(index, 1);
    }
  }

  async fetch<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    apiLogger.debug(`Fetch request to ${endpoint}`, {
      hasToken: !!this.token,
      tokenPreview: this.token ? this.token.substring(0, 20) + '...' : 'null',
    });
    return this.performFetch<T>(endpoint, options);
  }

  private async performFetch<T>(
    endpoint: string,
    options: FetchOptions = {}
  ): Promise<T> {
    const {
      token,
      headers = {},
      skipAuth = false,
      silentErrors = false,
      expectedStatuses,
      timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
      ...restOptions
    } = options;

    // Read from AuthTokenService.getTokenSync() first — its in-memory cache is
    // seeded from the `auth_token` cookie at module-import time, so the token
    // is available BEFORE any React useEffect runs (i.e. before
    // `AuthTokenService.initialize()` has had a chance to emit `token:updated`
    // into ApiClient.this.token). Fall back to `this.token` for the case where
    // a brand-new token was set this session (login/register/social callbacks)
    // — both paths set inMemoryToken via AuthTokenService.setToken, so in
    // practice this fallback is just defence-in-depth.
    const authToken = skipAuth
      ? null
      : token || AuthTokenService.getTokenSync() || this.token;

    let config: RequestInit & { url: string } = {
      ...restOptions,
      url: `${apiUrl()}/api${endpoint}`,
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        ...(this.locale && { 'Accept-Language': this.locale }),
        ...(authToken && { Authorization: `Bearer ${authToken}` }),
        ...headers,
        // Client version headers for version control
        'X-Client-Version': process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
        'X-Client-Platform': 'web',
        ...getDeviceHeaders(),
      },
      credentials: 'include',
    };

    // Apply request interceptors
    for (const interceptor of this.interceptors) {
      if (interceptor.onRequest) {
        config = await interceptor.onRequest(config);
      }
    }

    // Deadline is armed AFTER the interceptors so their (async) work doesn't eat
    // into the request's own budget, and released in `finally` on every path.
    const deadline =
      timeoutMs > 0 ? createDeadline(config.signal, timeoutMs) : null;
    if (deadline) {
      config = { ...config, signal: deadline.signal };
    }

    try {
      const response = await fetch(config.url, config);

      if (!response.ok) {
        let error: ApiError;

        try {
          error = (await response.json()) as ApiError;
        } catch {
          // If response is not JSON, create a generic error
          error = {
            message: `Request failed with status ${response.status}: ${response.statusText}`,
          };
        }

        // Handle 426 Upgrade Required - app version too old
        if (response.status === 426 && typeof window !== 'undefined') {
          const meta = error.errors?.[0]?.meta as
            | Record<string, string>
            | undefined;
          window.dispatchEvent(
            new CustomEvent('upgrade-required', {
              detail: {
                currentVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
                minVersion: meta?.min_version || 'unknown',
                updateUrl: meta?.update_url || window.location.origin,
              },
            })
          );
        }

        const apiError = new ApiClientError(error, response.status);

        // Apply error interceptors
        for (const interceptor of this.interceptors) {
          if (interceptor.onError) {
            await interceptor.onError(apiError);
          }
        }

        // Log API errors for monitoring (except expected ones).
        // silentErrors demotes to debug — used for endpoints where 404 is part
        // of the expected response surface (e.g. loading-facts cold-load).
        if (
          response.status >= 500 ||
          (response.status >= 400 &&
            response.status !== 401 &&
            response.status !== 422)
        ) {
          const log =
            silentErrors || expectedStatuses?.includes(response.status)
              ? apiLogger.debug.bind(apiLogger)
              : apiLogger.error.bind(apiLogger);
          log(`API Error: ${endpoint} [${response.status}]`, {
            status: response.status,
            method: options.method || 'GET',
            error,
          });
        }

        throw apiError;
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return {} as T;
      }

      return response.json();
    } catch (error) {
      // Aborted request (caller navigated away, a superseding refresh replaced
      // it, or the component unmounted). This is intentional cancellation, NOT
      // a failure — rethrow as-is so it never becomes a status=0 "Network Error"
      // log, never runs error interceptors, and TanStack Query skips retry/error
      // state for it. Mirrors Apple's APIError.userCancelled / Android's
      // CancellationException rethrow.
      // Deadline breach — the request hung past its budget. Distinguished from
      // the cancellation above by `didTimeout` (both surface as AbortError) and
      // converted into a normal retryable failure so the UI leaves its loading
      // state instead of spinning forever.
      if (deadline?.didTimeout()) {
        const method = options.method || 'GET';
        const timeoutError = new ApiClientError(
          { message: `Request timed out after ${timeoutMs}ms` },
          0,
          { timeout: true }
        );

        for (const interceptor of this.interceptors) {
          if (interceptor.onError) {
            await interceptor.onError(timeoutError);
          }
        }

        const log = silentErrors
          ? apiLogger.debug.bind(apiLogger)
          : apiLogger.error.bind(apiLogger);
        log(`Request timeout: ${endpoint}`, { method, timeoutMs });

        if (!silentErrors) {
          reportTimeoutToSentry(endpoint, method, timeoutMs);
        }

        throw timeoutError;
      }

      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      // Convert network errors to ApiClientError
      if (!(error instanceof ApiClientError)) {
        const apiError = new ApiClientError(
          {
            message:
              error instanceof Error ? error.message : 'Network error occurred',
          },
          0
        );

        // Apply error interceptors
        for (const interceptor of this.interceptors) {
          if (interceptor.onError) {
            await interceptor.onError(apiError);
          }
        }

        // Log network errors for monitoring (silentErrors demotes to debug).
        const log = silentErrors
          ? apiLogger.debug.bind(apiLogger)
          : apiLogger.error.bind(apiLogger);
        log(`Network Error: ${endpoint}`, {
          method: options.method || 'GET',
          error: error instanceof Error ? error.message : String(error),
        });

        throw apiError;
      }

      throw error;
    } finally {
      deadline?.release();
    }
  }

  async get<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.fetch<T>(endpoint, { ...options, method: 'DELETE' });
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: FetchOptions
  ): Promise<T> {
    return this.fetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * Confirm to the server that the low-balance nudge card was actually shown.
   * The server shows the nudge at most ONCE per descent into the low-balance
   * band and relies on this display-confirmation to arm that dedupe — so this
   * must fire when the nudge is rendered, not when the band is entered.
   *
   * Fire-and-forget and advisory: it returns 204 with no body, never blocks
   * UI, and swallows failures (a lost ack only risks one extra nudge show).
   */
  async acknowledgeTokenNudge(): Promise<void> {
    try {
      await this.post<void>('/v1/tokens/nudge/ack');
    } catch {
      // Advisory — a lost ack just risks one extra nudge show. Never surface.
    }
  }

  /**
   * Acknowledge the "save as reference" steer nudge was displayed.
   *
   * The server shows the nudge once per "Design #N" milestone and relies on
   * this display-confirmation to latch it — so this must fire when the banner
   * is actually rendered, not merely when the milestone concept is opened.
   *
   * Fire-and-forget and advisory: returns 204 with no body, never blocks UI,
   * and swallows failures (a lost ack only risks one extra nudge show).
   */
  async acknowledgeReferenceNudge(conceptId: string): Promise<void> {
    try {
      await this.post<void>(`/v1/concepts/${conceptId}/reference-nudge/ack`);
    } catch {
      // Advisory — a lost ack just risks one extra nudge show. Never surface.
    }
  }
}

/**
 * Machine-readable paywall codes. LimitErrorCode items are 422s that carry
 * their own PaywallPayload in the response body; SUBSCRIPTION_REQUIRED is a
 * 403 whose payload is pulled from the preflight-cached paywall_payloads map
 * on the effective plan. All codes share the same root-mounted paywall UI so
 * SUBSCRIPTION_REQUIRED renders identically to the other typed limits.
 * Locked by tests/Unit/Exceptions/LimitExceptionsTest.php — do not invent new
 * codes on the client; add them to the API contract first.
 */
export type LimitErrorCode =
  | 'ANGLE_LIBRARY_LIMIT_EXCEEDED'
  | 'CONCEPT_ANGLE_LIMIT_EXCEEDED'
  | 'PROJECT_LIMIT_EXCEEDED'
  | 'PROJECT_REFERENCE_LIMIT_EXCEEDED'
  | 'PROJECT_REFERENCE_INSUFFICIENT_TOKENS'
  | 'INSUFFICIENT_TOKENS'
  | 'RENDER_QUEUE_LIMIT_EXCEEDED'
  | 'RENDER_DAILY_LIMIT_EXCEEDED'
  | 'RENDER_WEEKLY_LIMIT_EXCEEDED'
  | 'RENDER_MONTHLY_LIMIT_EXCEEDED'
  | 'SUBSCRIPTION_REQUIRED'
  // Facade Pack pivot codes (all 422 with inline paywall payloads).
  | 'PACK_REQUIRED'
  | 'PACK_DESIGNS_EXHAUSTED'
  | 'PACK_ALBUMS_EXHAUSTED'
  | 'PACK_ESTIMATES_EXHAUSTED'
  | 'FREE_DESIGNS_EXHAUSTED';

/**
 * Structured metadata attached by the API to limit errors (422 + LimitErrorCode).
 * All fields optional — only the ones relevant to each specific code are set.
 */
export interface LimitErrorMeta {
  limit?: number;
  current?: number;
  project_id?: string | null;
  concept_id?: string;
  /** 'image' | 'document' */
  category_type?: string;
  /** PACK_REQUIRED: the gated FeatureSet capability key (e.g. cost_estimate). */
  capability?: string;
  /** PACK_*_EXHAUSTED: usage bands (plenty|low|exhausted) — never raw counters. */
  designs_band?: string | null;
  albums_band?: string | null;
  estimates_band?: string | null;
  /** FREE_DESIGNS_EXHAUSTED: whether the daily free quota refills tomorrow. */
  can_refill_tomorrow?: boolean;
}

/**
 * Server-driven paywall action kinds. Wire format locked by
 * apps/api/app/Enums/PaywallActionKindEnum.php — never invent new kinds here.
 * The catalog is server-driven, so an UNKNOWN kind can reach an older client;
 * consumers must degrade gracefully (treat the action as a dismiss) instead
 * of crashing.
 */
export type PaywallActionKind =
  | 'purchase_tokens'
  | 'delete_existing'
  | 'open_pass_paywall'
  // Facade Pack pivot: pack offer (no target). On WEB this renders as a
  // direct-to-mobile-app direction — web has no purchases. (Cap extensions
  // were removed; hitting a cap is now an info-only paywall with no action.)
  | 'purchase_pack'
  | 'dismiss';

export interface PaywallAction {
  kind: PaywallActionKind;
  label: string;
  target: string | null;
  /**
   * Optional short display name for a product tile in a multi-product chooser
   * (e.g. "Pro Pass"), overriding the client's catalog lookup. Undefined/null →
   * fall back to the catalog product name.
   */
  product_name?: string | null;
}

/**
 * Server-driven paywall payload for a typed limit error.
 *
 * All copy (title/body/action labels) comes from the API and is already
 * localized. Frontends render this block verbatim — no client-side
 * copywriting for limit paywalls.
 */
/**
 * Optional benefit bullet rendered above the token packs on a paywall.
 * Server-driven, already localized. `icon` is a semantic key
 * (`target`|`lightbulb`|older `sparkles`|`angle`|`architect`|`blueprint`)
 * the frontend maps to a local Lucide icon — never an asset URL.
 * `image_url`, when set, supersedes the icon: the client renders that image
 * (typically a small circular thumbnail) in place of the glyph. Used on the
 * token paywall so the returning-buyer progress bullet shows the user's own
 * last render instead of a generic "target" — personal cue, not stock icon.
 * Falls back to the icon when undefined OR when the image fails to load.
 * Absent/empty bullets array means the payload carries no bullets at all.
 */
export interface PaywallBullet {
  icon: string;
  label: string;
  image_url?: string | null;
}

/**
 * A/B experiment arm the server bucketed the user into (null when no experiment
 * is live). Distinct from the storefront `catalog_variant` segment: `key` is
 * the experiment identity, `variant` the arm. Echoed into paywall analytics;
 * never drives UI.
 */
export interface PaywallExperiment {
  key: string;
  variant: string;
}

export interface PaywallPayload {
  title: string;
  body: string;
  /**
   * Plain product brand name for the offer card in the ACTION (limit) context —
   * the DB `pack_products.name` WITHOUT the "Additional " storefront modifier
   * (e.g. "Facade Kit", not "Additional Facade Kit"). Present only on payloads
   * surfaced by a limit hit (PACK_REQUIRED / FREE_DESIGNS_EXHAUSTED). Clients
   * prefer this over the store-catalog pack name so the action paywall drops
   * the "Additional" framing (which belongs to deliberate storefront browse).
   * Optional/additive — absent on older payloads and on storefront browse.
   */
  product_name?: string;
  /**
   * Optional server-driven note rendered near the title/CTA when non-empty
   * (default ""). E.g. FREE_DESIGNS_EXHAUSTED carries "Out of free designs for
   * today. Unlock everything now, for good." Already localized; render
   * verbatim. Empty string means no note.
   */
  note?: string;
  /**
   * Server-driven advisory shown when the visitor's country is one Paddle
   * cannot sell to at all (e.g. RU/BY — the checkout would dead-end). Rendered
   * as a NoticeBanner near the buy CTA on pack-offering paywalls; already
   * localized, render verbatim. Empty/absent means no notice.
   */
  web_payment_notice?: string;
  /**
   * Affirmative CTA, or null for an info-only paywall — the limit can't be
   * relieved by an action here (daily/monthly render caps, or the user is
   * already on the top Pass). When null the dialog shows only the dismiss
   * button, styled as the single primary full-width button.
   */
  primary_action: PaywallAction | null;
  /** Null when the limit cannot be relieved by deleting items (e.g. render queue). */
  secondary_action: PaywallAction | null;
  dismiss_action: PaywallAction;
  /**
   * Lower-emphasis product offers rendered BELOW the primary CTA — distinct
   * from `secondary_action` (a relief gesture like delete/activate). The
   * multi-product INSUFFICIENT_TOKENS paywall uses it to offer, alongside the
   * primary token top-up, the "unlimited on this building forever" Facade Kit
   * (`purchase_pack`) and the contractor Pro Plan (`open_pass_paywall`).
   * Each routes by `kind` through the same handler as the primary/secondary
   * actions. Optional/additive — absent/empty on payloads without alternatives.
   */
  alternative_actions?: PaywallAction[];
  /**
   * Server-driven benefit bullets shown above the token packs (e.g. the
   * INSUFFICIENT_TOKENS payload carries 2 — icons `target` + `sparkles`).
   * Optional — absent/empty on payloads that don't curate bullets. Copy
   * already localized.
   */
  bullets?: PaywallBullet[];
  /**
   * Server-selected layout. Defaults to `"default"`. Render the default layout
   * for any unknown value (the client ships only the default today) — this
   * tolerance lets a future template ship to new builds while old ones degrade
   * gracefully. Optional/additive.
   */
  template?: string;
  /**
   * A/B arm, or null/absent when no experiment is live. Echo into paywall
   * analytics; do not branch UI on it. Optional/additive.
   */
  experiment?: PaywallExperiment | null;
  /**
   * Trust microcopy emitted on every paywall payload — the platform twins for
   * the "Secure payment via …" line under the CTA. Apple reads the apple key,
   * Android the google key, web the paddle key (web checks out through Paddle).
   * Optional/additive — empty/absent on older payloads.
   */
  secure_payment_apple?: string;
  secure_payment_google?: string;
  secure_payment_paddle?: string;
  /** T-Bank twin of the paddle line — web picks it when the active web rail's
   *  descriptor provider is `tbank`. */
  secure_payment_tbank?: string;
}

export class ApiClientError extends Error {
  /** `errors[0].code` — machine-readable error code (may be undefined). */
  public readonly code: string | undefined;
  /** `errors[0].meta` — structured data for the error (limit, current, ids, …). */
  public readonly meta: LimitErrorMeta | undefined;
  /**
   * `errors[0].paywall` — server-driven limit paywall content (title/body/actions).
   * Present only on typed 422 limit responses. Frontends render it verbatim.
   */
  public readonly paywall: PaywallPayload | undefined;
  /**
   * True when the request breached its client-side deadline rather than
   * failing on the wire. Callers use it to distinguish "the connection is
   * wedged, retrying may help" from a plain offline/DNS failure.
   */
  public readonly timeout: boolean;

  constructor(
    public readonly apiError: ApiError,
    public readonly status: number,
    options?: { timeout?: boolean }
  ) {
    const first = apiError.errors?.[0];
    const paywall = (first as { paywall?: PaywallPayload } | undefined)?.paywall;
    // Prefer paywall.body for user-facing message on typed limit errors so the
    // thrown Error carries the same copy the dialog will show.
    const errorMessage =
      paywall?.body || first?.detail || apiError.message || 'An error occurred';
    super(errorMessage);
    this.name = 'ApiClientError';
    this.code = first?.code;
    this.meta = first?.meta as LimitErrorMeta | undefined;
    this.paywall = paywall;
    this.timeout = options?.timeout === true;
  }

  getPaywall(): PaywallPayload | undefined {
    return this.paywall;
  }

  getFirstError(): string {
    if (this.apiError.errors && this.apiError.errors.length > 0) {
      const firstError = this.apiError.errors[0];
      return firstError?.detail || 'An error occurred';
    }
    return this.apiError.message || 'An error occurred';
  }

  getFieldErrors(): Record<string, string> {
    const fieldErrors: Record<string, string> = {};

    if (this.apiError.errors) {
      this.apiError.errors.forEach((error) => {
        if (error.source?.pointer) {
          const fieldName = error.source.pointer.split('/').pop() || '';
          fieldErrors[fieldName] = error.detail;
        }
      });
    }

    return fieldErrors;
  }

  isNetworkError(): boolean {
    return this.status === 0;
  }

  /** Request hit the client-side deadline (see DEFAULT_REQUEST_TIMEOUT_MS). */
  isTimeoutError(): boolean {
    return this.timeout;
  }

  isAuthError(): boolean {
    return this.status === 401;
  }

  isValidationError(): boolean {
    return this.status === 422;
  }

  isNotFoundError(): boolean {
    return this.status === 404;
  }

  isServerError(): boolean {
    return this.status >= 500;
  }

  isQueueLimitError(): boolean {
    return (
      this.status === 403 &&
      (this.apiError.errors?.some(
        (error) => error.detail === 'generation.exception.queue_limit'
      ) ||
        false)
    );
  }

  isSubscriptionRequiredError(): boolean {
    return (
      this.status === 403 &&
      (this.apiError.errors?.some(
        (error) => error.code === 'SUBSCRIPTION_REQUIRED'
      ) ||
        false)
    );
  }

  // Match server-emitted `code` rather than English `detail` so the helper
  // works regardless of the response Accept-Language. The set covers the
  // generic `INSUFFICIENT_TOKENS` (402, apps/api/.../InsufficientTokensException.php)
  // plus the project-reference-specific 422 variant from the limit-paywall router.
  isInsufficientTokensError(): boolean {
    return (
      (this.status === 402 || this.status === 422) &&
      (this.apiError.errors?.some(
        (error) =>
          error.code === 'INSUFFICIENT_TOKENS' ||
          error.code === 'PROJECT_REFERENCE_INSUFFICIENT_TOKENS'
      ) ||
        false)
    );
  }

  isForbiddenError(): boolean {
    return this.status === 403;
  }

  // Server codes from RenderDailyLimitException + TestAccountLimitException;
  // see apps/api/app/Exceptions/{RenderDailyLimitException,TestAccountLimitException}.php.
  isDailyLimitError(): boolean {
    return (
      this.status === 403 &&
      (this.apiError.errors?.some(
        (error) =>
          error.code === 'RENDER_DAILY_LIMIT_EXCEEDED' ||
          error.code === 'TEST_ACCOUNT_RENDER_DAILY_LIMIT_EXCEEDED' ||
          error.code === 'TEST_ACCOUNT_ALBUM_DAILY_LIMIT_EXCEEDED'
      ) ||
        false)
    );
  }

  // Additional error checking methods
  isUnauthorizedError(): boolean {
    return this.status === 401;
  }

  isRateLimitError(): boolean {
    return this.status === 429;
  }

  isUpgradeRequiredError(): boolean {
    return this.status === 426;
  }

  isPaymentError(): boolean {
    return (
      this.status === 402 ||
      this.apiError.errors?.some(
        (error) =>
          error.detail?.includes('payment') ||
          error.detail?.includes('billing') ||
          error.detail?.includes('subscription')
      ) ||
      false
    );
  }

  /**
   * DRY helper: check whether the machine-readable error code matches.
   * Frontends route paywall UI off `this.code` rather than detail strings so
   * messages stay API-driven (and localizable on the server).
   */
  private isLimitCode(code: LimitErrorCode): boolean {
    return this.code === code;
  }

  isAngleLibraryLimitError(): boolean {
    return this.isLimitCode('ANGLE_LIBRARY_LIMIT_EXCEEDED');
  }

  isConceptAngleLimitError(): boolean {
    return this.isLimitCode('CONCEPT_ANGLE_LIMIT_EXCEEDED');
  }

  isProjectLimitError(): boolean {
    return this.isLimitCode('PROJECT_LIMIT_EXCEEDED');
  }

  isProjectReferenceLimitError(): boolean {
    return this.isLimitCode('PROJECT_REFERENCE_LIMIT_EXCEEDED');
  }

  isRenderQueueLimitError(): boolean {
    return this.isLimitCode('RENDER_QUEUE_LIMIT_EXCEEDED');
  }

  /**
   * Cross-concept view-delete conflict (409): the view still has renders in or
   * is the main of other concepts. Not a limit/paywall — the client shows a
   * strategy-choice dialog and retries with `conflict_strategy`.
   */
  isAngleDeleteConflictError(): boolean {
    return this.status === 409 && this.code === 'ANGLE_DELETE_CONFLICT';
  }

  /** Typed `meta` for an ANGLE_DELETE_CONFLICT response (undefined otherwise). */
  getAngleDeleteConflict(): AngleDeleteConflictMeta | undefined {
    if (!this.isAngleDeleteConflictError()) return undefined;
    return this.meta as unknown as AngleDeleteConflictMeta;
  }

  getAllErrors(): string[] {
    if (this.apiError.errors && this.apiError.errors.length > 0) {
      return this.apiError.errors.map((error) => error.detail);
    }
    return [this.apiError.message || 'An error occurred'];
  }

  get statusCode(): number {
    return this.status;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
